import { useState, useCallback, useRef, useEffect } from 'react';
import { X, Bed, Users, Check, Calendar, DollarSign, Info, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import { parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import useEmblaCarousel from 'embla-carousel-react';

/**
 * Render a `YYYY-MM-DD` string as a locale date without letting the runtime
 * interpret it as UTC midnight (which shifts to the previous local day in any
 * negative-offset timezone — the "December 25 – 30" that customers see for a
 * December 26 – 31 stay). `parseISO` parses a bare date as LOCAL midnight,
 * matching the pattern already used in `src/utils/formatScheduleDate.js`.
 */
const formatStayDate = (value) => {
  const ymd = String(value ?? '').slice(0, 10);
  if (!ymd || ymd.length < 10) return '';
  const parsed = parseISO(ymd);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString();
};

const RoomRatesModal = ({ hotel, onSelectRate, onClose }) => {
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [hotelGalleryRef, hotelGalleryApi] = useEmblaCarousel({ loop: true });
  const modalContentRef = useRef(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  const scrollHotelPrev = useCallback(() => {
    if (hotelGalleryApi) hotelGalleryApi.scrollPrev();
  }, [hotelGalleryApi]);

  const scrollHotelNext = useCallback(() => {
    if (hotelGalleryApi) hotelGalleryApi.scrollNext();
  }, [hotelGalleryApi]);

  useEffect(() => {
    if (selectedRateId && modalContentRef.current) {
      modalContentRef.current.scrollTo({
        top: modalContentRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [selectedRateId]);

  if (!hotel || !hotel.rooms || hotel.rooms.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-2xl w-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-light text-white">Room Selection</h2>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          <p className="text-white/60">No rooms available for this hotel.</p>
        </div>
      </div>
    );
  }

  const handleSelectRate = () => {
    const selectedRate = hotel.rooms
      .flatMap(room => room.rates)
      .find(rate => rate.id === selectedRateId);

    if (selectedRate) {
      onSelectRate(selectedRate);
      onClose();
    }
  };

  const formatBeds = (beds) => {
    if (!beds || beds.length === 0) return 'Bed configuration varies';
    return beds.map(bed => `${bed.count} ${bed.type} bed${bed.count > 1 ? 's' : ''}`).join(', ');
  };

  const formatBoardType = (boardType) => {
    const boardTypes = {
      'room_only': 'Room Only',
      'breakfast': 'Breakfast Included',
      'half_board': 'Half Board',
      'full_board': 'Full Board',
      'all_inclusive': 'All Inclusive'
    };
    return boardTypes[boardType] || boardType;
  };

  const formatCancellation = (timeline) => {
    if (!timeline || timeline.length === 0) return 'Non-refundable';
    const firstDeadline = timeline[0];
    if (firstDeadline.refund_amount === '0.00' || firstDeadline.refund_amount === 0) {
      return 'Non-refundable';
    }
    const date = new Date(firstDeadline.before);
    return `Free cancellation until ${date.toLocaleDateString()}`;
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[9999] flex items-start justify-center px-2 sm:px-4 pt-24 sm:pt-28 pb-4">
      <div ref={modalContentRef} className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-4xl w-full max-h-full overflow-y-auto">
        <div className="bg-[#0a0a0a] border-b border-white/10 p-3 sm:p-4 md:p-6 rounded-t-2xl">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl sm:text-2xl font-light text-white mb-1">{hotel.name}</h2>
              <p className="text-xs sm:text-sm text-white/60">{hotel.address}</p>
              <div className="flex items-center gap-2 sm:gap-4 mt-2">
                <div className="flex items-center gap-1">
                  <Calendar size={12} className="text-white/60" />
                  <span className="text-xs text-white/60">
                    {formatStayDate(hotel.check_in_date)} - {formatStayDate(hotel.check_out_date)}
                  </span>
                </div>
                <div className="text-xs text-white/60">
                  {hotel.nights} night{hotel.nights !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/60 hover:text-white transition-colors flex-shrink-0"
            >
              <X size={20} className="sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Hotel Photo Gallery */}
        {hotel.images && hotel.images.length > 0 && (
          <div className="relative">
            <div className="overflow-hidden" ref={hotelGalleryRef}>
              <div className="flex">
                {hotel.images.map((imageUrl, index) => (
                  <div key={index} className="flex-[0_0_100%] min-w-0">
                    <div 
                      className="aspect-video cursor-pointer group relative"
                      onClick={() => setLightboxImage(imageUrl)}
                    >
                      <img
                        src={imageUrl}
                        alt={`${hotel.name} - Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23374151" width="800" height="600"/%3E%3C/svg%3E';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                        <Maximize2 className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Carousel Navigation */}
            {hotel.images.length > 1 && (
              <>
                <button
                  onClick={scrollHotelPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={scrollHotelNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-all"
                >
                  <ChevronRight size={24} />
                </button>
                
                {/* Photo counter */}
                <div className="absolute bottom-4 right-4 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full">
                  {hotel.images.length} photos
                </div>
              </>
            )}
          </div>
        )}

        <div className="p-3 md:p-6 space-y-4">
          {hotel.rooms.flatMap((room, roomIndex) => 
            room.rates.map((rate) => {
              const isSelected = selectedRateId === rate.id;
              const savings = parseFloat(rate.public_amount || 0) - parseFloat(rate.total_amount || 0);
              const hasSavings = savings > 0;

              return (
                <div
                  key={rate.id}
                  className={`
                    rounded-xl border transition-all overflow-hidden
                    ${isSelected 
                      ? 'bg-white/5 border-white/20' 
                      : 'bg-white/[0.02] border-white/[0.08]'
                    }
                  `}
                >
                  {/* Room Title - Outside clickable area */}
                  <div className="px-4 pt-4 pb-2">
                    <h3 className="text-lg font-medium text-white mb-1">{rate.room_name || room.name}</h3>
                    <p className="text-sm text-white/60">{formatBeds(rate.beds || room.beds)}</p>
                  </div>

                  {/* Rate Container - No longer clickable */}
                  <div className="w-full">
                    <div className="flex flex-col md:flex-row items-start gap-4 p-4 pt-0">
                      {/* Room Photos - LEFT SIDE on desktop, TOP on mobile */}
                      <div className="w-full md:w-80 md:flex-shrink-0">
                        <RoomPhotoGallery 
                          photos={rate.photos || room.photos} 
                          roomName={rate.room_name || room.name}
                          onImageClick={setLightboxImage}
                        />
                      </div>

                      {/* Rate Details - RIGHT SIDE on desktop, BOTTOM on mobile */}
                      <div className="flex-1 w-full flex flex-col md:flex-row items-start justify-between gap-4">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white/90">
                              {formatBoardType(rate.board_type)}
                            </span>
                            {rate.deal_types && rate.deal_types.length > 0 && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                                Deal
                              </span>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-white/60">
                            <div className="flex items-center gap-1">
                              <Users size={12} />
                              <span>{rate.payment_type === 'pay_now' ? 'Pay Now' : 'Pay Later'}</span>
                            </div>
                            <div className="flex items-center gap-1 relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTooltip(activeTooltip === `cancel-${rate.id}` ? null : `cancel-${rate.id}`);
                                }}
                                className="flex items-center gap-1 hover:text-white transition-colors"
                              >
                                <Info size={12} />
                                <span>{formatCancellation(rate.cancellation_timeline)}</span>
                              </button>
                              {activeTooltip === `cancel-${rate.id}` && (
                                <div className="absolute bottom-full left-0 mb-2 w-64 bg-black/95 border border-white/20 rounded-lg p-3 text-xs text-white z-50 shadow-xl">
                                  <div className="font-medium mb-2">Cancellation Policy</div>
                                  {rate.cancellation_timeline && rate.cancellation_timeline.length > 0 ? (
                                    rate.cancellation_timeline.map((timeline, idx) => (
                                      <div key={idx} className="mb-1">
                                        Before {new Date(timeline.before).toLocaleString()}: ${timeline.refund_amount} refund
                                      </div>
                                    ))
                                  ) : (
                                    <div>This rate is non-refundable</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {rate.conditions && rate.conditions.length > 0 && (
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveTooltip(activeTooltip === `policy-${rate.id}` ? null : `policy-${rate.id}`);
                                }}
                                className="flex items-center gap-1 text-xs text-white/60 hover:text-white transition-colors"
                              >
                                <Info size={12} />
                                <span>Guarantee & Policies</span>
                              </button>
                              {activeTooltip === `policy-${rate.id}` && (
                                <div className="absolute top-full left-0 mt-2 w-80 bg-black/95 border border-white/20 rounded-lg p-3 text-xs text-white z-50 shadow-xl max-h-64 overflow-y-auto">
                                  <div className="font-medium mb-2">Room Policies</div>
                                  {rate.conditions.map((condition, idx) => (
                                    <div key={idx} className="mb-2">
                                      <div className="font-medium text-white/90">{condition.title}</div>
                                      <div className="text-white/70">{condition.description}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {rate.quantity_available > 0 && rate.quantity_available <= 5 && (
                            <div className="text-xs text-brand-400 font-normal">
                              Only {rate.quantity_available} left at this price
                            </div>
                          )}
                        </div>

                        <div className="text-left md:text-right flex-shrink-0 w-full md:w-auto flex flex-col items-start md:items-end">
                          {hasSavings && (
                            <div className="text-xs text-white/40 line-through mb-1">
                              ${parseFloat(rate.public_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                          <div className="text-2xl font-light text-white mb-1">
                            ${parseFloat(rate.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <div className="text-xs text-white/60">
                            ${(parseFloat(rate.total_amount) / hotel.nights).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/night
                          </div>
                          {/* Due at Hotel notice - mandatory fees payable at check-in */}
                          {rate.due_at_accommodation_amount && rate.due_at_accommodation_amount > 0 && (
                            <div className="mt-2 px-2.5 py-1.5 bg-brand-500/10 border border-brand-500/20 rounded-lg">
                              <div className="text-xs text-brand-400 font-normal">
                                + ${parseFloat(rate.due_at_accommodation_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} due at hotel
                              </div>
                              <div className="text-[10px] text-brand-400/70">
                                Mandatory fee paid at check-in
                              </div>
                            </div>
                          )}
                          {hasSavings && (
                            <div className="text-xs text-emerald-400 mt-1">
                              Save ${savings.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </div>
                          )}
                          <Button
                            onClick={() => setSelectedRateId(rate.id)}
                            className={`
                              mt-3 px-5 sm:px-6 py-2 sm:py-2.5 rounded-full transition-all text-sm font-medium
                              ${isSelected
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30'
                                : 'bg-white text-black hover:bg-white/90'
                              }
                            `}
                          >
                            {isSelected ? (
                              <span className="flex items-center gap-2">
                                <Check size={16} />
                                Selected
                              </span>
                            ) : (
                              'Select Room'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer - Only show when a room is selected */}
        {selectedRateId && (
          <div className="bg-[#0a0a0a] border-t border-white/10 p-3 sm:p-4 md:p-6 rounded-b-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs sm:text-sm text-white/60 text-center sm:text-left">
                Room and rate selected
              </div>
              <div className="flex items-center justify-center sm:justify-end gap-2 sm:gap-3">
                <Button
                  onClick={onClose}
                  className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 sm:py-2.5 rounded-full bg-white/5 border border-white/10 text-white hover:bg-white/10 transition-all text-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSelectRate}
                  className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 sm:py-2.5 rounded-full bg-white text-black hover:bg-white/90 transition-all text-sm font-medium"
                >
                  Continue to Review
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Lightbox for expanded images */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-[99999] flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white hover:text-white/70 transition-colors z-10"
          >
            <X size={32} />
          </button>
          <img
            src={lightboxImage}
            alt="Enlarged view"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

// Room Photo Gallery Component
const RoomPhotoGallery = ({ photos, roomName, onImageClick }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  if (!photos || photos.length === 0) {
    return (
      <div className="rounded-lg bg-white/5 border border-white/10 flex items-center justify-center h-56">
        <Bed size={32} className="text-white/30" />
      </div>
    );
  }

  return (
    <div className="relative rounded-lg overflow-hidden">
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {photos.map((photo, index) => {
            const photoUrl = typeof photo === 'object' ? photo.url : photo;
            return (
              <div key={index} className="flex-[0_0_100%] min-w-0">
                <div 
                  className="h-56 cursor-pointer group relative"
                  onClick={(e) => {
                    e.stopPropagation();
                    onImageClick(photoUrl);
                  }}
                >
                  <img
                    src={photoUrl}
                    alt={`${roomName} - Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23374151" width="800" height="600"/%3E%3C/svg%3E';
                    }}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                    <Maximize2 className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Carousel Navigation */}
      {photos.length > 1 && (
        <>
          <div
            onClick={(e) => {
              e.stopPropagation();
              scrollPrev();
            }}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-all z-10 cursor-pointer"
          >
            <ChevronLeft size={18} />
          </div>
          <div
            onClick={(e) => {
              e.stopPropagation();
              scrollNext();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition-all z-10 cursor-pointer"
          >
            <ChevronRight size={18} />
          </div>
          
          {/* Photo counter */}
          <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full">
            {photos.length} photos
          </div>
        </>
      )}
    </div>
  );
};

export default RoomRatesModal;
